/* eslint-disable */
import { MarkerType } from '@xyflow/react';
import { edgeStyles } from './NodeStyles';

// Validate if a connection is allowed between node types
export const isConnectionValid = (sourceNode, targetNode, nodes) => {
  const sourceType = nodes.find((n) => n.id === sourceNode)?.type;
  const targetType = nodes.find((n) => n.id === targetNode)?.type;

  // Connection rules:
  // 1. start -> supervisor/agent (workflow starts with supervisor or agent)
  // 2. supervisor -> agent (supervisor can connect to agents horizontally)
  // 3. supervisor -> end (supervisor can connect to end vertically)
  // 4. agent -> agent/tool/end (agents can call other agents, tools, or end)
  // 5. tool -> agent (tools return output to agents)
  // 6. No connections to start
  // 7. No connections from end
  // 8. No self connections

  if (targetType === 'start' || sourceType === 'end') {
    console.log('Invalid: Cannot connect to start or from end');
    return false;
  }

  // Prevent self connections
  if (sourceNode === targetNode) {
    console.log('Invalid: Cannot connect node to itself');
    return false;
  }

  switch (sourceType) {
    case 'start':
      // Start can connect to supervisor or agent vertically
      console.log('Start node connecting to:', targetType);
      return targetType === 'supervisor' || targetType === 'agent';
    case 'agent':
      // Agent can connect to agent, tool, or end vertically
      console.log('Agent connecting to:', targetType);
      return ['agent', 'tool', 'end'].includes(targetType);
    case 'supervisor':
      // Supervisor can connect to agent horizontally or end vertically
      console.log('Supervisor connecting to:', targetType);
      return targetType === 'agent' || targetType === 'end';
    case 'tool':
      // Tool can only connect back to agent vertically
      console.log('Tool connecting to:', targetType);
      return targetType === 'agent';
    default:
      console.log('Invalid source type:', sourceType);
      return false;
  }
};

// Check if an edge already exists
export const checkEdgeExists = (source, target, edges) => {
  return edges.some(
    (edge) => (edge.source === source && edge.target === target) || (edge.source === target && edge.target === source)
  );
};

// Check if there's a connection in the opposite direction
export const hasReverseConnection = (source, target, edges) => {
  return edges.some((e) => e.source === target && e.target === source);
};

// Add debugging function to help identify connection issues
export const logConnectionDetails = (source, target, nodes, edges) => {
  const sourceNode = nodes.find((n) => n.id === source);
  const targetNode = nodes.find((n) => n.id === target);

  if (!sourceNode || !targetNode) {
    console.error('Source or target node not found:', { source, target });
    return;
  }

  const sourceType = sourceNode.type;
  const targetType = targetNode.type;

  console.log(`Connection details:`, {
    source: { id: source, type: sourceType },
    target: { id: target, type: targetType },
    existingConnections: edges.filter((e) => e.source === source || e.target === target)
  });
};

// Create a new edge with proper styling
export const createNewEdge = (params, sourceType, targetType, isConditionalEdge, hasReverse) => {
  const newEdgeId = `edge-${params.source}-${params.target}-${Date.now()}`;

  // Get the appropriate color based on source and target node types
  let edgeColor;
  if (sourceType === 'agent' && targetType === 'tool') {
    edgeColor = edgeStyles.colors.agent;
  } else if (sourceType === 'tool') {
    edgeColor = edgeStyles.colors.tool;
  } else if (sourceType === 'start') {
    edgeColor = edgeStyles.colors.start;
  } else if (sourceType === 'end') {
    edgeColor = edgeStyles.colors.end;
  } else if (sourceType === 'supervisor') {
    edgeColor = edgeStyles.colors.supervisor;
  } else {
    edgeColor = edgeStyles.colors.agent;
  }

  // Log connection details for debugging
  console.log('Creating edge:', {
    source: params.source,
    target: params.target,
    sourceType,
    targetType,
    isConditional: isConditionalEdge,
    sourceHandle: params.sourceHandle,
    targetHandle: params.targetHandle
  });

  // Prepare the label for conditional edges
  const edgeLabel = isConditionalEdge ? 'condition' : '';

  // Default condition expression for conditional edges
  const conditionExpression = isConditionalEdge ? 'true' : null;

  // Determine if this is a horizontal connection (supervisor to agent)
  const isHorizontalConnection = sourceType === 'supervisor' && targetType === 'agent';

  // For supervisor to agent connections, ensure correct handle mapping
  let sourceHandle = params.sourceHandle;
  let targetHandle = params.targetHandle;

  if (isHorizontalConnection) {
    // If coming from supervisor's left, connect to agent's right
    if (params.sourceHandle === 'left') {
      targetHandle = 'right';
    }
    // If coming from supervisor's right, connect to agent's left
    else if (params.sourceHandle === 'right') {
      targetHandle = 'left';
    }
  }

  // Make supervisor-agent connections bidirectional by default
  const isBidirectional = isHorizontalConnection || hasReverse;

  return {
    id: newEdgeId,
    source: params.source,
    target: params.target,
    sourceHandle: sourceHandle,
    targetHandle: targetHandle,
    type: 'custom',
    animated: true,
    style: {
      stroke: edgeColor,
      strokeDasharray: isConditionalEdge ? edgeStyles.dashed.strokeDasharray : 'none'
    },
    data: {
      conditional: isConditionalEdge,
      label: isHorizontalConnection ? 'send/receive' : edgeLabel,
      sourceType,
      targetType,
      bidirectional: isBidirectional,
      conditionExpression,
      isHorizontalConnection,
      // Add agentConfig for supervisor-agent connections
      agentConfig: isHorizontalConnection
        ? {
            id: params.target,
            type: 'agent',
            config: {
              model: {
                provider: 'openai',
                model: 'gpt-4o',
                temperature: 0.7,
                maxTokens: 1000
              },
              systemPrompt: 'You are an agent that receives tasks from the supervisor and executes them.',
              template: '{{input}}',
              memory: {
                type: 'buffer',
                maxMessages: 10,
                reflectionThreshold: 5
              },
              toolCalling: {
                enabled: false,
                toolChoice: 'auto'
              },
              functions: [],
              state: {
                variables: []
              }
            }
          }
        : null
    },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: edgeColor
    },
    markerStart: isBidirectional
      ? {
          type: MarkerType.ArrowClosed,
          color: edgeColor
        }
      : undefined
  };
};

// Update edge to be bidirectional
export const updateEdgeToBidirectional = (edge, targetType, sourceType) => {
  // Determine edge color based on types
  let edgeColor;
  if (sourceType === 'agent' && targetType === 'tool') {
    edgeColor = edgeStyles.colors.agent; // Use agent color
  } else if (sourceType === 'supervisor' && targetType === 'agent') {
    edgeColor = edgeStyles.colors.supervisor; // Use agent color
  } else if (targetType === 'tool') {
    edgeColor = edgeStyles.colors.tool;
  } else {
    edgeColor = edgeStyles.colors.agent;
  }

  return {
    ...edge,
    type: 'custom',
    data: {
      ...edge.data,
      bidirectional: true
    },
    markerStart: {
      type: MarkerType.ArrowClosed,
      color: edgeColor
    }
  };
};

export const convertToMarkedDownString = (content, AIEnableMarkdown = true) => {
  try {
    if (AIEnableMarkdown) {
      const checkCharExist = typeof content === 'string' && content && content.trim();
      if (checkCharExist && checkCharExist.length > 0) {
        // return marked.parse(content);
        // eslint-disable-next-line no-undef
        const parsedData = marked.parse(content.replace(/ {4}/g, '%%%%'));
        return parsedData.replaceAll('%%%%', '    ');
        /* const parsedData = marked.parse(content.replace(/ {4}/g, '%%%%').replaceAll('`', '@@').replaceAll('\\', '@!'));
          return parsedData.replaceAll('%%%%', '    ').replaceAll('@@', '`').replaceAll('@!', '\\').replaceAll('<em>', '_')
            .replaceAll('</em>', '_'); */
      }

      return checkCharExist;
    }

    return content;
  } catch {
    console.log('error in converting markdown');

    return content;
  }
};

export const loadExternalJS = () => {
  const scriptElement = document.createElement('script');
  scriptElement.type = 'text/javascript';
  scriptElement.src = 'https://cdn.jsdelivr.net/npm/marked/marked.min.js';
  scriptElement.async = true;
  scriptElement.onload = () => {
    // Custom tokenizer for inline math delimiters: \( ... \) and \[ ... \]
    const mathTokenizer = {
      name: 'MATH_EXPRESSION',
      level: 'inline',
      // eslint-disable-next-line consistent-return
      tokenizer(src) {
        // Match inline math with \( ... \)
        const inlineMathMatch = /^\\\((.*?)\\\)/.exec(src);
        if (inlineMathMatch) {
          return {
            type: 'MATH_EXPRESSION',
            subType: 'LATEX_BRACKET',
            raw: inlineMathMatch[0],
            text: inlineMathMatch[1],
            tokens: []
          };
        }

        // Match inline math with \[ ... \] (treat as inline math)
        const blockMathMatch = /^\\\[(.*?)\\\]/s.exec(src); // Added "s" for dot-all mode to match multi-line
        if (blockMathMatch) {
          return {
            type: 'MATH_EXPRESSION',
            subType: 'LATEX_SQ_BRACKET',
            raw: blockMathMatch[0],
            text: blockMathMatch[1],
            tokens: []
          };
        }
      },

      // eslint-disable-next-line consistent-return
      renderer(token) {
        // Render both inline and block delimiters as inline math
        if (token.type === 'MATH_EXPRESSION' && token.subType === 'LATEX_BRACKET') {
          return `\\(${token.text}\\)`; // Render inline math
        }
        if (token.type === 'MATH_EXPRESSION' && token.subType === 'LATEX_SQ_BRACKET') {
          return `\\[${token.text}\\]`; // Render inline math
        }
      }
    };
    // custom math extension to Marked.js
    // eslint-disable-next-line no-undef
    marked.use({ extensions: [mathTokenizer] });
  };
  document.body.appendChild(scriptElement);
};
